import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useState, useRef, useCallback } from 'react'
import { Schemas } from '~/api/gen'
import { Divider } from '~/components/elements/Divider'
import { NoResults } from '~/components/elements/NoResults'
import { AppLeadingDialog } from '~/components/modules/HalfModal/AppLeadingDialog'
import { HalfModal } from '~/components/modules/HalfModal/HalfModal'
import { Pagination } from '~/components/modules/Pagination/Pagination'
import { useBaseUrl } from '~/features/shared/domain/context'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { usePaginationProps } from '~/hooks/usePaginationProps'
import { useSessionStorage } from '~/hooks/useSessionStorage'
import { MEMBER_DETAIL_HALF_MODAL_ADJUST_URL } from '~/lib/constants'
import { useCoordinateItemsByCoordinateIds } from '../shared/coordinateItem/hooks/useCoordinateItemsByCoordinateIds'
import { FrequentlyUsedCoordinateItems } from '../shared/memberDetail/components/FrequentlyUsedCoordinateItems'
import { FrequentlyUsedTags } from '../shared/memberDetail/components/FrequentlyUsedTags'
import { MemberDetailLayout } from '../shared/memberDetail/components/MemberDetailLayout'
import { Coordinates } from './components/Coordinates'
import { FETCH_COUNT_PER_PAGE } from './constants'
import {
  sendGtmEvAppRedirectHalfModalButton,
  sendGtmEvShowHalfModal,
} from './lib/gtmEvent'

export type Props = {
  member: Schemas.member_large_detail
  coordinateCollection: Schemas.get_members_user_name_coordinates_v1_response
}

// eslint-disable-next-line complexity
export const MemberDetail: NextPage<Props> = ({
  member,
  coordinateCollection,
}) => {
  const router = useRouter()
  const currentPage = Number(router.query.pageno) || 1
  const isFirstPage = currentPage === 1
  const isDesktop = useIsDesktop()
  const frequentlyUsedTagsExist = member.frequently_used_tags.length > 0
  const frequentlyUsedCoordinateItemsExist =
    member.frequently_used_coordinate_items.length > 0

  const paginationProps = usePaginationProps(
    currentPage,
    Math.ceil(
      Math.min(
        coordinateCollection.total_count,
        coordinateCollection.max_count
      ) / FETCH_COUNT_PER_PAGE
    ),
    false
  )

  const { data: coordinateItems, isLoading: isLoadingCoordinateItems } =
    useCoordinateItemsByCoordinateIds(
      isDesktop
        ? coordinateCollection.coordinates.map((coordinate) => coordinate.id)
        : null
    )

  //ハーフモーダルの表示ロジック
  const baseUrl = useBaseUrl().toString()
  const {
    value: showedAppRedirectHalfModal,
    set: setShowedAppRedirectHalfModal,
    isReady: isReadySessionStorage,
  } = useSessionStorage('showed_app_redirect_half_modal_on_member_detail')
  const [isHalfModalOpen, setIsHalfModalOpen] = useState(false)
  const closeHalfModal = useCallback(() => setIsHalfModalOpen(false), [])
  if (
    isReadySessionStorage &&
    !showedAppRedirectHalfModal &&
    document.referrer.includes(baseUrl)
  ) {
    setShowedAppRedirectHalfModal(true)
    setIsHalfModalOpen(true)
  }
  const sentGtmEvShowHalfModalRef = useRef(false)
  if (!isDesktop && isHalfModalOpen && !sentGtmEvShowHalfModalRef.current) {
    sendGtmEvShowHalfModal()
    sentGtmEvShowHalfModalRef.current = true
  }

  const isNoResults = coordinateCollection.total_count === 0

  return (
    <>
      <MemberDetailLayout
        pageType="coordinates"
        member={member}
        currentPage={currentPage}
      >
        {!isNoResults ? (
          <>
            <div className={isFirstPage ? '' : 'xl:pt-5'}>
              <Coordinates
                coordinates={coordinateCollection.coordinates}
                coordinateItems={coordinateItems?.coordinate_items}
                isLoadingCoordinateItems={isLoadingCoordinateItems}
              />
            </div>
            <div className="mt-5">
              <Pagination {...paginationProps} />
            </div>
            {(frequentlyUsedTagsExist ||
              frequentlyUsedCoordinateItemsExist) && (
              <>
                <div
                  className={`hidden xl:block ${isNoResults ? 'xl:pt-[70px]' : 'xl:pt-7'}`}
                >
                  <Divider />
                </div>
                <section className="grid gap-y-[22px] px-4 pt-10 xl:gap-y-7 xl:px-0 xl:py-[30px]">
                  {frequentlyUsedTagsExist && (
                    <FrequentlyUsedTags
                      nickName={member.nick_name}
                      frequentlyUsedTags={member.frequently_used_tags}
                    />
                  )}
                  {frequentlyUsedCoordinateItemsExist && (
                    <FrequentlyUsedCoordinateItems
                      nickName={member.nick_name}
                      frequentlyUsedCoordinateItems={
                        member.frequently_used_coordinate_items
                      }
                    />
                  )}
                </section>
              </>
            )}
          </>
        ) : (
          <NoResults
            className="pb-[38px] pt-[33px] xl:pb-[90px] xl:pt-9"
            type="COORDINATE"
          >
            まだ投稿はありません
          </NoResults>
        )}
      </MemberDetailLayout>
      <HalfModal
        isOpen={!isDesktop && isHalfModalOpen}
        onClose={closeHalfModal}
      >
        <AppLeadingDialog
          title={'アプリで\n気になる人をフォローしよう'}
          message={
            'アプリなら気になるユーザーの\nフォローやいいね、コメントができます。'
          }
          buttonText="アプリで開く"
          href={MEMBER_DETAIL_HALF_MODAL_ADJUST_URL}
          target="_blank"
          rel="noreferrer"
          onButtonClick={sendGtmEvAppRedirectHalfModalButton}
          onCloseClick={closeHalfModal}
        />
      </HalfModal>
    </>
  )
}
